import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import apiClient from "../../utils/apiClient";
import Layout from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  Check,
  Loader2,
  ArrowRight,
  Sparkles,
  Building2,
  Users,
  FileText,
  Globe,
  MessageCircle,
  Send,
  Mic,
  BarChart3,
  Shield,
  Headphones,
} from "lucide-react";

const FEATURE_ICONS = {
  workspace: Building2,
  users: Users,
  documents: FileText,
  web: Globe,
  whatsapp: MessageCircle,
  telegram: Send,
  voice: Mic,
  analytics: BarChart3,
  sso: Shield,
  support: Headphones,
};

export default function AgentPricing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadSubscription();
    }
  }, [isAuthenticated]);

  const loadPlans = async () => {
    try {
      const res = await apiClient.get("/agent/billing/plans");
      setPlans(res.data.plans);
    } catch (err) {
      console.error("Failed to load plans", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const res = await apiClient.get("/agent/billing/subscription");
      setCurrentSubscription(res.data);
    } catch (err) {
      console.error("Failed to load subscription", err);
    }
  };

  const handleSelectPlan = (tier) => {
    if (!isAuthenticated) {
      navigate("/login?redirect=/agent/pricing");
      return;
    }

    if (tier === "enterprise") {
      window.location.href = "mailto:sales@cbaiq.com?subject=Contract%20Expert%20Enterprise%20Inquiry";
      return;
    }

    setCheckoutLoading(tier);
    apiClient.post("/agent/billing/checkout", {
      plan_tier: tier,
      billing_interval: billingInterval,
      origin_url: window.location.origin,
    }).then(res => {
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    }).catch(err => {
      toast.error(err.response?.data?.detail || "Failed to create checkout session");
    }).finally(() => {
      setCheckoutLoading(null);
    });
  };

  const isCurrentPlan = (tier) => {
    return currentSubscription?.plan_tier === tier;
  };

  const getButtonText = (plan) => {
    if (isCurrentPlan(plan.tier)) {
      return "Current Plan";
    }
    if (plan.is_enterprise) {
      return "Contact Sales";
    }
    if (currentSubscription?.is_trial) {
      return "Upgrade Now";
    }
    return "Get Started";
  };

  const getAnnualSavings = (plan) => {
    if (plan.price_monthly === 0) return null;
    const monthlyCost = plan.price_monthly * 12;
    const savings = monthlyCost - plan.price_annual;
    const percentSaved = Math.round((savings / monthlyCost) * 100);
    return { amount: savings, percent: percentSaved };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12" data-testid="agent-pricing-page">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Your 24/7 Labor Relations Advisor
          </div>
          <h1 className="text-4xl font-bold mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg text-muted-foreground">
            Choose the plan that fits your team. No hidden fees.
            Ask unlimited questions — managers should never hesitate to get answers.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <button
            onClick={() => setBillingInterval("monthly")}
            data-testid="billing-toggle-monthly"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              billingInterval === "monthly"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval("annual")}
            data-testid="billing-toggle-annual"
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              billingInterval === "annual"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary hover:bg-secondary/80"
            }`}
          >
            Annual
            <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
              Save 17%
            </span>
          </button>
        </div>

        {/* Trial Banner */}
        {!currentSubscription || currentSubscription?.is_trial ? (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6 text-center mb-10">
            <h3 className="font-semibold text-lg mb-2">
              🎉 Start with a 14-Day Free Trial
            </h3>
            <p className="text-muted-foreground">
              Experience everything Contract Expert has to offer — full access to all Professional features.
              No credit card required to start.
            </p>
            {currentSubscription?.is_trial && currentSubscription?.trial_days_remaining && (
              <p className="mt-2 text-sm font-medium text-blue-600 dark:text-blue-400">
                {currentSubscription.trial_days_remaining} days remaining in your trial
              </p>
            )}
          </div>
        ) : null}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => {
            const savings = getAnnualSavings(plan);
            const price = billingInterval === "annual" 
              ? Math.round(plan.price_annual / 12) 
              : plan.price_monthly;

            return (
              <div
                key={plan.tier}
                className={`
                  relative rounded-2xl border-2 p-6 transition-all
                  ${plan.is_popular 
                    ? "border-primary bg-primary/5 scale-105 shadow-xl" 
                    : "border-border bg-card hover:border-primary/50"
                  }
                `}
                data-testid={`pricing-card-${plan.tier}`}
              >
                {/* Popular Badge */}
                {plan.is_popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </div>
                )}

                {/* Plan Header */}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {plan.description}
                  </p>
                  
                  {/* Price */}
                  <div className="mb-2">
                    {plan.is_enterprise ? (
                      <span className="text-3xl font-bold">Custom</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">${price}</span>
                        <span className="text-muted-foreground">/mo</span>
                      </>
                    )}
                  </div>
                  
                  {billingInterval === "annual" && savings && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      Save ${savings.amount}/year ({savings.percent}% off)
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  className="w-full"
                  variant={plan.is_popular ? "default" : "outline"}
                  size="lg"
                  disabled={isCurrentPlan(plan.tier) || checkoutLoading === plan.tier}
                  onClick={() => handleSelectPlan(plan.tier)}
                  data-testid={`select-plan-${plan.tier}`}
                >
                  {checkoutLoading === plan.tier ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {getButtonText(plan)}
                  {!isCurrentPlan(plan.tier) && !plan.is_enterprise && (
                    <ArrowRight className="w-4 h-4 ml-2" />
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Feature Comparison */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold text-center mb-8">
            Compare Plans
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="feature-comparison">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-4 px-4">Feature</th>
                  <th className="text-center py-4 px-4">Starter</th>
                  <th className="text-center py-4 px-4 bg-primary/5">Professional</th>
                  <th className="text-center py-4 px-4">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Users</td>
                  <td className="text-center py-3 px-4">5</td>
                  <td className="text-center py-3 px-4 bg-primary/5">25</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Documents</td>
                  <td className="text-center py-3 px-4">50</td>
                  <td className="text-center py-3 px-4 bg-primary/5">500</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Questions</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                  <td className="text-center py-3 px-4 bg-primary/5">Unlimited</td>
                  <td className="text-center py-3 px-4">Unlimited</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Web Access</td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4 bg-primary/5"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">WhatsApp</td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-primary/5"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Telegram</td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-primary/5"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Voice Notes</td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-primary/5"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Advanced Analytics</td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-primary/5"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">SSO Ready</td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-primary/5 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">API Access</td>
                  <td className="text-center py-3 px-4 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4 bg-primary/5 text-muted-foreground">—</td>
                  <td className="text-center py-3 px-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4 font-medium">Support</td>
                  <td className="text-center py-3 px-4">Email</td>
                  <td className="text-center py-3 px-4 bg-primary/5">Priority</td>
                  <td className="text-center py-3 px-4">Dedicated</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-semibold text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-2">Can I change plans later?</h3>
              <p className="text-muted-foreground text-sm">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, 
                and we&apos;ll prorate the difference.
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-2">Are there any question limits?</h3>
              <p className="text-muted-foreground text-sm">
                No! We believe managers should never hesitate to ask questions. All plans include 
                unlimited questions to your Contract Expert.
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-2">What happens after my trial ends?</h3>
              <p className="text-muted-foreground text-sm">
                After your 14-day trial, you can choose a paid plan to continue using Contract Expert.
                Your data and documents will be preserved.
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-2">Do you offer refunds?</h3>
              <p className="text-muted-foreground text-sm">
                Yes, we offer a 30-day money-back guarantee. If you&apos;re not satisfied, contact us 
                and we&apos;ll refund your subscription.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
